import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { TX_COLORS, colorFor } from "@/lib/reportColors";

const GRANULARITY = ["day", "week", "month"];
const SERIES_BY = ["transaction_type", "employee", "department"];

export default function Tendencia() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [granularity, setGranularity] = useState("day");
  const [seriesBy, setSeriesBy] = useState("transaction_type");
  const [data, setData] = useState({ labels: [], series: [] });
  const [loading, setLoading] = useState(false);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .tendencia(payload, granularity, seriesBy)
      .then((r) => alive && setData(r.data || { labels: [], series: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, granularity, seriesBy]);

  const chartData = useMemo(() => {
    return (data.labels || []).map((lab, i) => {
      const row = { label: lab };
      (data.series || []).forEach((s) => (row[s.name] = s.data[i] || 0));
      return row;
    });
  }, [data]);

  return (
    <div className="space-y-6" data-testid="tendencia-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="tendencia-title">
          {t("tendencia.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("tendencia.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("tendencia.granularity")}</Label>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger data-testid="tendencia-granularity-select" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRANULARITY.map((g) => (
                <SelectItem key={g} value={g} data-testid={`tendencia-gran-${g}`}>
                  {t(`tendencia.${g}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("tendencia.seriesBy")}</Label>
          <Select value={seriesBy} onValueChange={setSeriesBy}>
            <SelectTrigger data-testid="tendencia-series-select" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SERIES_BY.map((s) => (
                <SelectItem key={s} value={s} data-testid={`tendencia-series-${s}`}>
                  {t(`tendencia.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("tendencia.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div
              className="py-16 text-center text-sm text-muted-foreground"
              data-testid="tendencia-empty"
            >
              {loading ? t("common.loading") : t("common.no_data")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={chartData} margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                {(data.series || []).map((s, i) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={TX_COLORS[s.name] || colorFor(s.name, i)}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
